import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import apiService, { resolveFileUrl } from '../services/api';

// 管理端科技绿主题（与后台其他页面保持一致）
const ADMIN_COLORS = {
  primary: '#00ff9f',
  text: '#e4f1ff',
  textMuted: 'rgba(214, 227, 252, 0.55)',
  border: 'rgba(0, 255, 159, 0.25)',
  bgCard: 'rgba(0, 30, 60, 0.6)',
};

const DEFAULT_WIDGETS = [
  { title: '联系客服', image: '', enabled: true },
  { title: '使用教程', image: '', enabled: true },
];

const FloatingWidgetConfig = () => {
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS.map((w) => ({ ...w })));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const fileInputRefs = [useRef(null), useRef(null)];

  // 加载配置
  const loadConfig = async (showTip = false) => {
    setLoading(true);
    try {
      const res = await apiService.getFloatingWidgets();
      if (res && res.success && Array.isArray(res.widgets) && res.widgets.length === 2) {
        setWidgets(res.widgets.map((w) => ({
          title: w.title || '',
          image: w.image || '',
          enabled: !!w.enabled,
        })));
      }
      if (showTip) {
        setSnackbar({ open: true, message: '配置已刷新', type: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: '加载配置失败，请稍后重试', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // 修改字段
  const updateWidget = (index, field, value) => {
    setWidgets((prev) => prev.map((w, i) => (i === index ? { ...w, [field]: value } : w)));
  };

  // 选择文件并上传
  const handleSelectFile = (index, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: '图片大小不能超过 5MB', type: 'error' });
      return;
    }
    setUploadingIndex(index);
    apiService
      .uploadFloatingWidgetImage(file)
      .then((res) => {
        if (res && res.success && res.image) {
          updateWidget(index, 'image', res.image);
          setSnackbar({ open: true, message: '二维码上传成功，别忘了点击保存配置', type: 'success' });
        } else {
          setSnackbar({ open: true, message: (res && res.message) || '上传失败', type: 'error' });
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.userMessage || '上传失败，请稍后重试';
        setSnackbar({ open: true, message: msg, type: 'error' });
      })
      .finally(() => setUploadingIndex(null));
  };

  // 保存配置
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiService.saveFloatingWidgets(widgets);
      if (res && res.success) {
        setSnackbar({ open: true, message: '悬浮窗配置已保存，网站主页即时生效', type: 'success' });
      } else {
        setSnackbar({ open: true, message: (res && res.message) || '保存失败', type: 'error' });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.userMessage || '保存失败，请稍后重试';
      setSnackbar({ open: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 渲染单个悬浮窗配置卡片
  const renderWidgetCard = (index) => {
    const w = widgets[index];
    const uploading = uploadingIndex === index;
    return (
      <Paper
        elevation={0}
        sx={{
          background: ADMIN_COLORS.bgCard,
          border: `1px solid ${ADMIN_COLORS.border}`,
          borderRadius: '14px',
          p: 2.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          opacity: w.enabled ? 1 : 0.65,
          transition: 'opacity 0.3s',
        }}
      >
        {/* 卡片头部：编号 + 启用开关 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                background: 'linear-gradient(135deg, rgba(0, 255, 159, 0.25), rgba(0, 120, 255, 0.25))',
                border: `1px solid ${ADMIN_COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <QrCode2Icon sx={{ color: ADMIN_COLORS.primary, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: ADMIN_COLORS.text, fontSize: '0.95rem', fontWeight: 700 }}>
                悬浮窗{index === 0 ? '一' : '二'}
              </Typography>
              <Typography sx={{ color: ADMIN_COLORS.textMuted, fontSize: '0.7rem' }}>
                网站主页右下角第{index + 1}个
              </Typography>
            </Box>
          </Box>
          <Tooltip title={w.enabled ? '已启用，主页正常显示' : '已停用，主页将隐藏'} arrow>
            <FormControlLabel
              control={
                <Switch
                  checked={w.enabled}
                  onChange={(e) => updateWidget(index, 'enabled', e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: ADMIN_COLORS.primary },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      background: 'rgba(0, 255, 159, 0.35)',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: w.enabled ? ADMIN_COLORS.primary : ADMIN_COLORS.textMuted, fontSize: '0.78rem' }}>
                  {w.enabled ? '启用' : '停用'}
                </Typography>
              }
            />
          </Tooltip>
        </Box>

        {/* 标题输入框 */}
        <TextField
          fullWidth
          size="small"
          label="悬浮窗标题"
          value={w.title}
          onChange={(e) => updateWidget(index, 'title', e.target.value)}
          inputProps={{ maxLength: 20 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography sx={{ color: ADMIN_COLORS.textMuted, fontSize: '0.7rem' }}>
                  {w.title.length}/20
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.4)' },
              '&.Mui-focused fieldset': { borderColor: ADMIN_COLORS.primary },
            },
            '& .MuiInputLabel-root': { color: ADMIN_COLORS.textMuted, fontSize: '0.85rem' },
            '& .MuiOutlinedInput-input': { color: ADMIN_COLORS.text, fontSize: '0.9rem' },
          }}
        />

        {/* 二维码预览与上传 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <Box
            sx={{
              width: 176,
              height: 176,
              background: '#fff',
              borderRadius: '12px',
              p: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              border: '1px dashed rgba(0, 255, 159, 0.4)',
              position: 'relative',
            }}
          >
            {uploading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={26} sx={{ color: ADMIN_COLORS.primary }} />
                <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>上传中...</Typography>
              </Box>
            ) : w.image ? (
              <img
                src={resolveFileUrl(w.image)}
                alt={`悬浮窗${index + 1}二维码`}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Box
                onClick={() => fileInputRefs[index].current?.click()}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.8,
                  cursor: 'pointer',
                  color: '#94a3b8',
                }}
              >
                <UploadIcon sx={{ fontSize: 30, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                  点击上传二维码图片
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                  支持 png/jpg/webp，不超过5MB
                </Typography>
              </Box>
            )}
          </Box>

          {/* 上传/替换/移除按钮 */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
              disabled={uploading}
              onClick={() => fileInputRefs[index].current?.click()}
              sx={{
                borderColor: ADMIN_COLORS.border,
                color: ADMIN_COLORS.primary,
                fontSize: '0.78rem',
                '&:hover': { borderColor: ADMIN_COLORS.primary, background: 'rgba(0, 255, 159, 0.08)' },
              }}
            >
              {w.image ? '替换二维码' : '上传二维码'}
            </Button>
            {w.image && (
              <Tooltip title="移除当前二维码" arrow>
                <IconButton
                  size="small"
                  disabled={uploading}
                  onClick={() => updateWidget(index, 'image', '')}
                  sx={{
                    color: 'rgba(239, 68, 68, 0.75)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    '&:hover': { background: 'rgba(239, 68, 68, 0.12)' },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* 隐藏的文件选择框 */}
          <input
            ref={fileInputRefs[index]}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              handleSelectFile(index, file);
              e.target.value = '';
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      {/* 页面标题 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '11px',
              background: 'linear-gradient(135deg, rgba(0, 255, 159, 0.2), rgba(0, 120, 255, 0.2))',
              border: `1px solid ${ADMIN_COLORS.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <QrCode2Icon sx={{ color: ADMIN_COLORS.primary, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ color: ADMIN_COLORS.text, fontSize: '1.15rem', fontWeight: 700 }}>
              悬浮窗配置
            </Typography>
            <Typography sx={{ color: ADMIN_COLORS.textMuted, fontSize: '0.78rem' }}>
              配置网站主页右下角两个浮动弹窗的标题与二维码
            </Typography>
          </Box>
        </Box>
        <Tooltip title="重新加载当前配置" arrow>
          <IconButton
            onClick={() => loadConfig(true)}
            disabled={loading}
            sx={{ color: ADMIN_COLORS.textMuted, '&:hover': { color: ADMIN_COLORS.primary } }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* 提示信息 */}
      <Alert
        severity="info"
        icon={false}
        sx={{
          mb: 2.5,
          background: 'rgba(0, 120, 255, 0.1)',
          border: '1px solid rgba(0, 120, 255, 0.25)',
          borderRadius: '10px',
          '& .MuiAlert-message': { color: 'rgba(214, 227, 252, 0.85)', fontSize: '0.8rem', lineHeight: 1.7 },
        }}
      >
        保存后立即生效：已启用并配置二维码的悬浮窗将显示在网站主页（登录/注册页）右下角；
        用户点击「忘记密码」时，也会展示第一个启用悬浮窗的二维码用于联系人工重置密码。
      </Alert>

      {/* 配置卡片 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: ADMIN_COLORS.primary }} />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            {renderWidgetCard(0)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderWidgetCard(1)}
          </Grid>
        </Grid>
      )}

      {/* 保存按钮 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
        <Button
          variant="contained"
          disabled={loading || saving}
          startIcon={saving ? null : <SaveIcon sx={{ fontSize: 18 }} />}
          onClick={handleSave}
          sx={{
            background: 'linear-gradient(135deg, #00c176 0%, #00ff9f 100%)',
            color: '#001e3c',
            fontWeight: 700,
            px: 3.5,
            py: 1,
            borderRadius: '10px',
            fontSize: '0.9rem',
            boxShadow: '0 4px 16px rgba(0, 255, 159, 0.25)',
            '&:hover': { background: 'linear-gradient(135deg, #00d986 0%, #00ff9f 100%)' },
            '&.Mui-disabled': { background: 'rgba(0, 255, 159, 0.15)', color: 'rgba(0, 30, 60, 0.4)' },
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#001e3c' }} /> : '保存配置'}
        </Button>
      </Box>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{
            background: snackbar.type === 'success'
              ? 'linear-gradient(135deg, #00c176 0%, #00ff9f 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: snackbar.type === 'success' ? '#001e3c' : '#fff',
            fontWeight: 600,
            borderRadius: '10px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloatingWidgetConfig;
